/* eslint-disable max-lines-per-function */
import CardWrapper from '../wrapper/card';
import { ConnectDomainParams } from '../../params/redux/domain';
import CustomInput from '../reusable/input';
import { gray } from '../../theme/themePrimitives';
import ImageInput from '../reusable/input/file_input';
import LabelWrapper from '../wrapper/label';
import { siteSettingSchema } from '../../schema/domain_schema';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { Close, DriveFolderUpload } from '@mui/icons-material';
import { connectDomainThunk, updateDomainThunk } from '../../redux/thunks/domain';
import { Form, Formik } from 'formik';
import { settingIndexForConnectingDomain, settingIsConnectingFlagDomain } from '../../redux/slices/domain';
import { useDispatch, useSelector } from 'react-redux';

const SiteSettingsComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { flags, connectDomain, domain, loaders } = useSelector((state: RootState) => state.domain);
    const initialValues = {
        title: domain?.site_title || '',
        description: domain?.website_description || '',
        favicon: domain && domain?.favicon_image ? `${process.env.REACT_APP_FILE_PATH}${domain?.favicon_image}` : '',
        socialImage: domain && domain?.social_image ? `${process.env.REACT_APP_FILE_PATH}${domain?.social_image}` : ''
    };

    const nextStepSaveHandler = async (values: {
        title: string;
        description: string;
        favicon: string;
        socialImage: string;
    }) => {
        try {
            const data: ConnectDomainParams = {
                ...connectDomain,
                site_title: values.title,
                social_image: values.socialImage,
                favicon_image: values.favicon,
                website_description: values.description,
                domain_host: '@',
                domain_records: '76.76.21.12'
            };
            if (domain) {
                await dispatch(updateDomainThunk(data)).unwrap();
            } else {
                await dispatch(connectDomainThunk(data)).unwrap();
            }
            dispatch(settingIndexForConnectingDomain(0));
            dispatch(settingIsConnectingFlagDomain(false));
        } catch (e) {
            //
        }
    };

    const onCancelHandler = () => {
        dispatch(settingIsConnectingFlagDomain(false));
        dispatch(settingIndexForConnectingDomain(0));
    };

    const previousStepHandler = () => {
        dispatch(settingIndexForConnectingDomain(flags.selectedIndex - 1));
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={siteSettingSchema}
            onSubmit={nextStepSaveHandler}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                <Form>
                    <CardWrapper>
                        <Typography variant="subtitle1" fontWeight="bold" color="textSecondary" mb={1}>Site Settings</Typography>
                        <Box display="flex" justifyContent="space-between">
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <LabelWrapper label="Title" isMandatory={true} />
                                    <Typography variant="body1" mb={1}>Every website has a title that shows on google and browser tabs</Typography>
                                    <CustomInput
                                        value={values.title}
                                        onChange={handleChange('title')}
                                        onBlur={handleBlur('title')}
                                        error={touched.title && errors.title}
                                        placeholder="Enter your website title here"
                                        disabled={loaders.isCreating || loaders.isUpdating}
                                        fullWidth
                                    />
                                </Box>
                                <CustomInput
                                    type="textarea"
                                    value={values.description}
                                    onChange={handleChange('description')}
                                    onBlur={handleBlur('description')}
                                    error={touched.description && errors.description}
                                    label="Website Description"
                                    placeholder="Type here"
                                    disabled={loaders.isCreating || loaders.isUpdating}
                                    mandatoryFields={true}
                                    fullWidth
                                />
                                <Box>
                                    <LabelWrapper label="Fav Icon" isMandatory={true} />
                                    <Typography variant="body1" mb={1}>A favicon is a small icon next to your site title. Recommend size is 32 x 32 px</Typography>
                                    <ImageInput
                                        value={values.favicon}
                                        disabled={loaders.isCreating || loaders.isUpdating}
                                        onChange={(e) => setFieldValue('favicon', e)}
                                    />
                                </Box>
                                <Box>
                                    <LabelWrapper label="Social image" isMandatory={true} />
                                    <Typography variant="body1" mb={1}>This image will be shared on social networks including Facebook and<br /> Twitter. Recommend size is 1200 x 630 px</Typography>
                                    <ImageInput
                                        value={values.socialImage}
                                        disabled={loaders.isCreating || loaders.isUpdating}
                                        onChange={(e) => setFieldValue('socialImage', e)}
                                    />
                                </Box>
                            </Box>
                            <Box minWidth="500px" maxWidth="500px" display="flex" flexDirection="column" justifyContent="space-between">
                                <Box>
                                    <LabelWrapper label="Preview" />
                                    <CardWrapper>
                                        <Box display="flex" gap={2} mb={1}>
                                            <Box>
                                                {values.favicon
                                                    ? (
                                                        <Avatar
                                                            alt="favicon"
                                                            src={values.favicon}
                                                            sx={{ bgcolor: gray[200], border: `1px solid ${gray[500]}`, objectFit: 'contain' }}
                                                        />)
                                                    : <Avatar alt="Favicon" src="" sx={{ bgcolor: gray[200], border: `1px solid ${gray[500]}` }} >
                                                        <DriveFolderUpload />
                                                    </Avatar>}
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="600">{values.title || 'Website title'}</Typography>
                                                <Typography variant="body2">{connectDomain.domain_name}</Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="body1">{values.description || 'Website description'}</Typography>
                                    </CardWrapper>
                                </Box>
                                <Box>
                                    <LabelWrapper label="Fav icon preview" />
                                    <Box>
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 1.5,
                                                borderRadius: 2,
                                                width: 300,
                                                overflow: 'hidden',
                                                backgroundColor: '#f1f1f1'
                                            }}
                                        >
                                            {/* Left: Circle for Favicon */}
                                            <Box marginRight={2}>
                                                {values.favicon
                                                    ? <Avatar
                                                        alt="favicon"
                                                        src={values.favicon}
                                                        sx={{ bgcolor: gray[200], border: `1px solid ${gray[500]}`, objectFit: 'contain' }}
                                                    />
                                                    : <Avatar alt="Favicon" src="" sx={{ bgcolor: gray[200], border: `1px solid ${gray[500]}` }} >
                                                        <DriveFolderUpload />
                                                    </Avatar>}
                                            </Box>

                                            {/* Middle: Website Title */}
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ flexGrow: 1, color: 'text.primary', fontWeight: 500 }}
                                            >
                                                {values.title || 'Website title'}
                                            </Typography>

                                            {/* Right: Close Icon */}
                                            <IconButton
                                                size="small"
                                                onClick={() => null}
                                                aria-label="close favicon preview"
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    </Box>
                                </Box>
                                <Box>
                                    <LabelWrapper label="Social image preview" />
                                    <Box border="1px solid #ddd" borderRadius={1}>
                                        <Box bgcolor="#f1f1f1" display="flex" justifyContent="center" alignItems="center" color={gray[200]} p={1}>
                                            {values.socialImage
                                                ? <img src={values.socialImage} height="100%" width="100%" alt="Social Image" style={{ objectFit: 'contain' }} />
                                                : <DriveFolderUpload sx={{
                                                    height: '200px',
                                                    width: '100%'
                                                }} />}
                                        </Box>
                                        <Box display="flex" flexDirection="column" gap={1} bgcolor={gray[200]} p={2}>
                                            <Typography variant="body2">{connectDomain.domain_name}</Typography>
                                            <Typography variant="subtitle2" fontWeight="600">{values.title || 'Website title'}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box display="flex" gap={2} justifyContent="end" mt={4}>
                            {!domain && <Button variant="outlined" size="large" color="secondary" disabled={loaders.isCreating || loaders.isUpdating} onClick={previousStepHandler}>Previous</Button>}
                            {domain && <Button variant="outlined" size="large" color="secondary" disabled={loaders.isCreating || loaders.isUpdating} onClick={onCancelHandler}>Cancel</Button>}
                            <Button type="submit" variant="contained" size="large" disabled={loaders.isCreating || loaders.isUpdating}>
                                {domain ? 'Save' : 'Continue'}
                            </Button>
                        </Box>
                    </CardWrapper>
                </Form>
            )}</Formik>
    );
};

export default SiteSettingsComponent;